/**
 * Showing whatever was clicked.
 *
 * A run leaves NIfTI volumes, CSV tables, PNG figures, PDF reports and plain
 * text, and until now the only one of those the app could show was the volume.
 * Everything else went out to whatever the operating system felt like opening,
 * which means leaving the study to read your own results.
 *
 * Each kind gets the treatment it needs rather than a lowest common
 * denominator: a volume opens in the viewer, a table is a table with its
 * numbers right-aligned, a figure or a report opens at a readable size and zooms.
 * Where something genuinely cannot be shown, the pane says which file and why,
 * and offers the system viewer — it never shows an empty frame and calls that
 * a preview.
 */

import { useEffect, useMemo, useState } from "react";
import { ExternalLink, FileQuestion, Loader2 } from "lucide-react";

import { Button, EmptyState } from "@/components/ui/primitives";
import { getAssetUrl, openPath, readTextFile, revealPath, splitError } from "@/ipc/api";
import type { OutputNode } from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { numericColumns, parseCsv } from "./csv";
import { OutputVolumeViewer } from "./OutputVolumeViewer";
import { ZoomPane } from "./ZoomPane";

// The original (Tauri desktop) app rendered PDFs with pdf.js onto a canvas
// rather than handing them to an <iframe>, because WKWebView (macOS) bundles
// a PDF viewer but WebKitGTK (Linux) does not -- an <iframe> showed an empty
// frame there. That constraint is Tauri-embedded-webview-specific; it does
// not apply to this web build, where "the webview" is just whatever real
// browser the visitor is using, and every one of them (Chrome, Firefox,
// Safari, Edge) ships a native PDF viewer. Using it directly means no
// pdfjs-dist dependency at all -- found 2026-10-01: pdfjs-dist's worker
// import (`pdfjs-dist/build/pdf.worker.min.mjs?url`) reproducibly hung both
// the Vite dev server and a production build once it was anywhere in the
// eager import graph, regardless of version or lazy-loading. Simplest fix is
// to not carry the dependency in this build at all.
function PdfView({ url, name }: { url: string; name: string }) {
  return <iframe src={url} title={name} className="h-full w-full border-0 bg-white" />;
}

/** A read of whatever the file needs: a URL, or its text. */
type Loaded =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "url"; url: string }
  | { state: "text"; content: string; truncated: boolean }
  | { state: "error"; message: string };

/** 2 MB of a table is about 20 000 rows; more than anyone reads in a pane. */
const TEXT_LIMIT = 2_000_000;

function useFile(node: OutputNode | null, absPath: string | null): Loaded {
  const [loaded, setLoaded] = useState<Loaded>({ state: "idle" });
  useEffect(() => {
    // A volume resolves its own URL (base and overlays both) inside
    // `OutputVolumeViewer` — this hook is for everything else.
    if (!node || node.isDir || !absPath || node.kind === "nifti") {
      setLoaded({ state: "idle" });
      return;
    }
    let live = true;
    setLoaded({ state: "loading" });
    const wantsText = node.kind === "csv" || node.kind === "json" || node.kind === "txt";
    const job = wantsText
      ? readTextFile(absPath, TEXT_LIMIT).then(
          (r) => ({ state: "text", content: r.content, truncated: r.truncated }) as Loaded,
        )
      : getAssetUrl(absPath).then((r) => ({ state: "url", url: r.url }) as Loaded);

    job
      .then((next) => {
        if (live) setLoaded(next);
      })
      .catch((e) => {
        if (live) setLoaded({ state: "error", message: splitError(e).message });
      });
    return () => {
      live = false;
    };
  }, [node, absPath]);
  return loaded;
}

/** A figure, at its own size once known, so the pane can pick how to fit it. */
function ImageView({ url, name }: { url: string; name: string }) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => setSize({ w: 0, h: 0 }), [url]);
  return (
    <>
      {/* Loaded off-screen first: its natural size decides the first view. */}
      {size.w === 0 ? (
        <img
          src={url}
          alt=""
          className="hidden"
          onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
      ) : null}
      <ZoomPane width={size.w} height={size.h}>
        {(scale) => (
          <img
            src={url}
            alt={name}
            draggable={false}
            style={{ width: size.w * scale, height: size.h * scale, imageRendering: scale > 2 ? "pixelated" : "auto" }}
          />
        )}
      </ZoomPane>
    </>
  );
}

function TableView({ content, truncated }: { content: string; truncated: boolean }) {
  const table = useMemo(() => parseCsv(content, truncated), [content, truncated]);
  const numeric = useMemo(() => numericColumns(table), [table]);
  if (table.header.length === 0) {
    return <EmptyState title="Empty table" hint="The file has no rows." />;
  }
  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[11.5px]">
          <thead className="sticky top-0 bg-ads-surface">
            <tr>
              {table.header.map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  className={cn(
                    "border-b border-ads-border px-3 py-1.5 font-semibold text-neutral-700",
                    numeric[i] ? "text-right" : "text-left",
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r} className="even:bg-ads-subtle">
                {table.header.map((_, c) => (
                  <td
                    key={c}
                    className={cn(
                      "whitespace-nowrap border-b border-ads-border px-3 py-1 text-neutral-700",
                      numeric[c] ? "text-right tabular-nums" : "text-left",
                    )}
                  >
                    {row[c] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="shrink-0 border-t border-ads-border px-3 py-1 text-[10.5px] text-neutral-500">
        {table.rows.length} rows · {table.header.length} columns
        {/* A row that does not fit the header is said out loud rather than
            padded to look tidy. */}
        {table.ragged > 0 ? (
          <span className="ml-2 text-amber-700">
            {table.ragged} row{table.ragged === 1 ? "" : "s"} do not match the header
          </span>
        ) : null}
        {table.truncated ? (
          <span className="ml-2 text-amber-700">
            showing the first {(TEXT_LIMIT / 1e6).toFixed(0)} MB
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function FileViewer({
  node,
  absPath,
  onOpenVolume,
  siblingVolumes,
  sourceRoot,
}: {
  node: OutputNode | null;
  absPath: string | null;
  /** NIfTI goes to the viewer, which lives outside this pane. */
  onOpenVolume?: (path: string) => void;
  /** Every NIfTI file beside `node`, for the shared viewer's overlay picker. */
  siblingVolumes?: OutputNode[];
  /** `sources[i].path` the siblings' `relPath` is relative to. */
  sourceRoot?: string;
}) {
  // Non-volume kinds still need the plain asset-URL-or-text read.
  const loaded = useFile(node, absPath);

  if (!node) {
    return (
      <EmptyState
        title="Nothing selected"
        hint="Pick a file on the left. Volumes open in the viewer; tables, figures and reports open here."
      />
    );
  }
  if (node.isDir) {
    return <EmptyState title={node.name} hint="A folder. Open it to see what is inside." />;
  }

  const inFinder = (
    <div className="flex shrink-0 items-center gap-2 border-t border-ads-border px-3 py-1.5">
      <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-neutral-400">
        {node.relPath}
      </span>
      <Button size="sm" variant="outline" onClick={() => void openPath(absPath!).catch(() => undefined)}>
        <ExternalLink className="h-3 w-3" />
        Open
      </Button>
      <Button size="sm" variant="outline" onClick={() => void revealPath(absPath!).catch(() => undefined)}>
        Show in Finder
      </Button>
    </div>
  );

  if (node.kind === "nifti") {
    const siblings = (siblingVolumes ?? [node]).filter((n) => n.kind === "nifti");
    return (
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          {sourceRoot ? (
            <OutputVolumeViewer
              siblings={siblings}
              sourceRoot={sourceRoot}
              initialBaseRelPath={node.relPath}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t border-ads-border px-3 py-1.5">
          <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] text-neutral-400">
            {node.relPath}
          </span>
          {/* The workspace, for when the question is a reading question:
              overlays, linked panes, measurement. */}
          <Button size="sm" variant="outline" onClick={() => absPath && onOpenVolume?.(absPath)}>
            Open in the viewer
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void revealPath(absPath!).catch(() => undefined)}
          >
            Show in Finder
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-hidden">
        {loaded.state === "loading" ? (
          <div className="flex h-full items-center justify-center text-neutral-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : loaded.state === "error" ? (
          <div className="flex h-full items-center justify-center p-6">
            <EmptyState title="Could not show this file" hint={loaded.message} />
          </div>
        ) : loaded.state === "url" && (node.kind === "png") ? (
          <ImageView url={loaded.url} name={node.name} />
        ) : loaded.state === "url" && node.kind === "pdf" ? (
          <PdfView url={loaded.url} name={node.name} />
        ) : loaded.state === "text" && node.kind === "csv" ? (
          <TableView content={loaded.content} truncated={loaded.truncated} />
        ) : loaded.state === "text" ? (
          <pre className="h-full overflow-auto whitespace-pre-wrap break-words px-4 py-3 font-mono text-[11.5px] leading-relaxed text-neutral-700">
            {loaded.content}
            {loaded.truncated ? "\n\n… truncated" : ""}
          </pre>
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <EmptyState
              title={node.name}
              hint={`No preview for ${node.kind} files. Open it with whatever normally handles them.`}
            />
          </div>
        )}
      </div>
      {inFinder}
    </div>
  );
}

export { FileQuestion };
